// Support ticket detail (PX-218) — message + attachments, status controls,
// timeline (internal notes / sent push replies / compensations), token
// compensation, push reply, and the ticket author's snapshot with per-
// generation refund. No SMTP exists — the email path is a mailto: link
// that opens the admin's own mail client with a template.

import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { hasAnyPerm } from '@/lib/perms'
import { PERM } from '@/types/api'
import type {
  AdminRefundResponse,
  AdminSupportCompensateRequest,
  AdminSupportCompensateResponse,
  AdminSupportNote,
  AdminSupportNoteCreate,
  AdminSupportPushReplyRequest,
  AdminSupportPushReplyResponse,
  AdminSupportTicketDetail,
  AdminSupportTicketUpdate,
  SupportTicketStatus,
} from '@/types/api'
import { SupportStatusPill } from './Support'

const STATUSES: SupportTicketStatus[] = ['new', 'in_progress', 'answered', 'closed']

// Canned replies — client-side only. The Apple template covers money
// refunds: App Store payments can only be refunded by Apple, our side is
// token compensation.
const TEMPLATES: { label: string; body: string }[] = [
  {
    label: 'Issue fixed',
    body: 'Hi! Thanks for reaching out — the issue you reported has been fixed. Please update to the latest version and try again. — Pinxie Support',
  },
  {
    label: 'Tokens compensated',
    body: 'Hi! Sorry for the trouble — we have added bonus tokens to your account as compensation. They never expire. — Pinxie Support',
  },
  {
    label: 'Apple refund (money)',
    body: 'Hi! Purchases are processed by Apple, so money refunds can only be issued by them. Please open https://reportaproblem.apple.com, sign in with your Apple ID and request a refund for the purchase. — Pinxie Support',
  },
  {
    label: 'Need more details',
    body: 'Hi! Could you share a bit more detail — what you tapped, what you expected, and a screenshot if possible? That will help us fix it quickly. — Pinxie Support',
  },
]

const NOTE_KIND_META: Record<AdminSupportNote['kind'], { label: string; tone: string }> = {
  note: { label: 'internal note', tone: 'bg-ink-100 text-ink-500' },
  push_reply: { label: 'push reply', tone: 'bg-brand-50 text-brand-700' },
  compensation: { label: 'compensation', tone: 'bg-emerald-100 text-emerald-800' },
}

export function SupportTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const canWrite = hasAnyPerm(profile, [PERM.support_write])

  const detailQuery = useQuery({
    queryKey: ['admin', 'support', 'detail', ticketId],
    queryFn: () => request<AdminSupportTicketDetail>(`/admin/support/${ticketId}`),
    enabled: !!ticketId,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'support'] })
  }

  const statusMutation = useMutation({
    mutationFn: (status: SupportTicketStatus) =>
      request<AdminSupportTicketDetail>(`/admin/support/${ticketId}`, {
        method: 'PATCH',
        body: { status } satisfies AdminSupportTicketUpdate,
      }),
    onSuccess: invalidate,
  })

  const noteMutation = useMutation({
    mutationFn: (body: string) =>
      request<AdminSupportNote>(`/admin/support/${ticketId}/notes`, {
        method: 'POST',
        body: { body } satisfies AdminSupportNoteCreate,
      }),
    onSuccess: () => { setNoteDraft(''); invalidate() },
  })

  const compensateMutation = useMutation({
    mutationFn: (payload: AdminSupportCompensateRequest) =>
      request<AdminSupportCompensateResponse>(`/admin/support/${ticketId}/compensate`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => { setCompensateAmount(''); setCompensateReason(''); invalidate() },
  })

  const pushMutation = useMutation({
    mutationFn: (payload: AdminSupportPushReplyRequest) =>
      request<AdminSupportPushReplyResponse>(`/admin/support/${ticketId}/push-reply`, {
        method: 'POST',
        body: payload,
      }),
    onSuccess: () => { setReplyDraft(''); invalidate() },
  })

  const refundMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) =>
      request<AdminRefundResponse>(`/admin/generations/${id}/refund`, {
        method: 'POST',
        body: { amount },
      }),
    onSuccess: invalidate,
  })

  const [replyDraft, setReplyDraft] = useState('')
  const [noteDraft, setNoteDraft] = useState('')
  const [compensateAmount, setCompensateAmount] = useState('')
  const [compensateReason, setCompensateReason] = useState('')

  const ticket = detailQuery.data

  const mailtoHref = useMemo(() => {
    if (!ticket?.email) return null
    const subject = encodeURIComponent('Re: your Pinxie support request')
    const body = encodeURIComponent(replyDraft || '')
    return `mailto:${ticket.email}?subject=${subject}&body=${body}`
  }, [ticket?.email, replyDraft])

  if (detailQuery.isLoading) {
    return <div className="p-6 text-ink-400">Loading…</div>
  }
  if (!ticket) {
    return (
      <div className="p-6">
        <p className="text-ink-500">Ticket not found.</p>
        <button type="button" onClick={() => navigate('/support')} className="mt-2 text-sm text-brand-700 hover:underline">
          ← Back to inbox
        </button>
      </div>
    )
  }

  const ctx = ticket.user_context
  const canPush = canWrite && !!ctx && ctx.device_token_count > 0
  const mutationError = [statusMutation, noteMutation, compensateMutation, pushMutation, refundMutation]
    .map((m) => m.error)
    .find((e) => e instanceof APIError) as APIError | undefined

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/support')} className="mb-3 text-sm text-brand-700 hover:underline">
        ← Back to inbox
      </button>

      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Ticket</h1>
        <SupportStatusPill status={ticket.status} />
        <span className="text-sm text-ink-400">
          {new Date(ticket.created_at).toLocaleString()} · {ticket.platform}
          {ticket.app_version && ` · v${ticket.app_version}`}
        </span>
        {ticket.linear_issue_url && (
          <a href={ticket.linear_issue_url} target="_blank" rel="noreferrer" className="text-sm text-brand-700 hover:underline">
            Linear issue ↗
          </a>
        )}
      </header>

      {mutationError && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {mutationError.message}
        </div>
      )}
      {pushMutation.data && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Push delivered to {pushMutation.data.delivered} device{pushMutation.data.delivered === 1 ? '' : 's'}
          {pushMutation.data.failed > 0 && `, failed for ${pushMutation.data.failed}`}.
        </div>
      )}
      {compensateMutation.data && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Granted +{compensateMutation.data.amount} tokens — new balance {compensateMutation.data.new_credits_balance}.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        {/* Left column: message, reply, timeline */}
        <div className="flex flex-col gap-4">
          <Section title="Message">
            <div className="mb-2 text-sm text-ink-500">
              From <span className="font-medium text-ink-700">{ticket.email ?? 'no email'}</span>
              {!ticket.user_id && <span className="ml-1 text-ink-400">(anonymous web)</span>}
            </div>
            <p className="whitespace-pre-wrap text-sm text-ink-900">{ticket.message}</p>
            {ticket.attachment_urls.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {ticket.attachment_urls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="block">
                    <img src={url} alt="attachment" className="h-24 w-24 rounded-lg border border-ink-100 object-cover" />
                  </a>
                ))}
              </div>
            )}
          </Section>

          {canWrite && (
            <Section title="Reply">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const t = TEMPLATES.find((x) => x.label === e.target.value)
                    if (t) setReplyDraft(t.body)
                    e.target.value = ''
                  }}
                  className="rounded-md border border-ink-200 bg-white px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none"
                >
                  <option value="" disabled>Insert template…</option>
                  {TEMPLATES.map((t) => (
                    <option key={t.label} value={t.label}>{t.label}</option>
                  ))}
                </select>
                {ctx && (
                  <span className="text-xs text-ink-400">
                    {ctx.device_token_count > 0
                      ? `${ctx.device_token_count} device${ctx.device_token_count === 1 ? '' : 's'} for push`
                      : 'no devices — push unavailable'}
                  </span>
                )}
              </div>
              <textarea
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder="Write a reply… (push max 500 chars)"
                className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
              <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                {mailtoHref && (
                  <a
                    href={mailtoHref}
                    className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300"
                  >
                    Open in mail ↗
                  </a>
                )}
                <button
                  type="button"
                  disabled={!canPush || !replyDraft.trim() || pushMutation.isPending}
                  onClick={() => pushMutation.mutate({ body: replyDraft.trim() })}
                  title={canPush ? undefined : 'The author has no registered devices'}
                  className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {pushMutation.isPending ? 'Sending…' : 'Send push'}
                </button>
              </div>
            </Section>
          )}

          <Section title="Timeline">
            {ticket.notes.length === 0 && <p className="text-sm text-ink-400">No notes yet.</p>}
            <ul className="flex flex-col gap-3">
              {ticket.notes.map((note) => {
                const meta = NOTE_KIND_META[note.kind]
                return (
                  <li key={note.id} className="rounded-lg border border-ink-100 p-3">
                    <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-ink-400">
                      <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-medium ${meta.tone}`}>{meta.label}</span>
                      <span>{note.author_email ?? 'system'}</span>
                      <span>{new Date(note.created_at).toLocaleString()}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-ink-700">{note.body}</p>
                  </li>
                )
              })}
            </ul>
            {canWrite && (
              <form
                className="mt-3 flex items-start gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  if (noteDraft.trim()) noteMutation.mutate(noteDraft.trim())
                }}
              >
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={2}
                  maxLength={4000}
                  placeholder="Internal note (user never sees this)…"
                  className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
                <button
                  type="submit"
                  disabled={!noteDraft.trim() || noteMutation.isPending}
                  className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:border-ink-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add
                </button>
              </form>
            )}
          </Section>
        </div>

        {/* Right column: status, compensation, author snapshot */}
        <div className="flex flex-col gap-4">
          {canWrite && (
            <Section title="Status">
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={statusMutation.isPending || ticket.status === s}
                    onClick={() => statusMutation.mutate(s)}
                    className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                      ticket.status === s
                        ? 'bg-ink-900 text-white'
                        : 'border border-ink-200 text-ink-700 hover:border-ink-300'
                    } disabled:cursor-not-allowed`}
                  >
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
              {ticket.status_changed_at && (
                <p className="mt-2 text-xs text-ink-400">
                  Last change {new Date(ticket.status_changed_at).toLocaleString()}. Answered tickets auto-close after 7 days.
                </p>
              )}
            </Section>
          )}

          {canWrite && ctx && (
            <Section title="Compensate tokens">
              <p className="mb-2 text-xs text-ink-400">
                Grants into the non-expiring purchased balance. Money refunds for App Store purchases are Apple-only — use the “Apple refund” reply template.
              </p>
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  const amount = Number(compensateAmount)
                  if (!Number.isInteger(amount) || amount <= 0 || amount > 10_000) return
                  if (!confirm(`Grant +${amount} tokens to ${ctx.email ?? ctx.user_id}?`)) return
                  compensateMutation.mutate({ amount, reason: compensateReason.trim() || undefined })
                }}
              >
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={compensateAmount}
                  onChange={(e) => setCompensateAmount(e.target.value)}
                  placeholder="Amount, tokens"
                  className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
                <input
                  type="text"
                  value={compensateReason}
                  onChange={(e) => setCompensateReason(e.target.value)}
                  maxLength={500}
                  placeholder="Reason (audit log)"
                  className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
                <button
                  type="submit"
                  disabled={compensateMutation.isPending || !compensateAmount}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {compensateMutation.isPending ? 'Granting…' : 'Grant tokens'}
                </button>
              </form>
            </Section>
          )}

          {ctx ? (
            <Section title="Author">
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-ink-400">Email</dt>
                <dd className="truncate text-ink-700">{ctx.email ?? '—'}</dd>
                <dt className="text-ink-400">Kind</dt>
                <dd className="text-ink-700">
                  {ctx.kind}
                  {!ctx.is_active && <span className="ml-1 text-red-600">inactive</span>}
                  {ctx.deleted_at && <span className="ml-1 text-red-600">deleted</span>}
                </dd>
                <dt className="text-ink-400">Plan</dt>
                <dd className="text-ink-700">{ctx.plan_name ?? ctx.plan_slug ?? 'free'}</dd>
                <dt className="text-ink-400">Tokens</dt>
                <dd className="tabular-nums text-ink-700">{ctx.credits_balance} purchased · {ctx.allowance_balance} allowance</dd>
                <dt className="text-ink-400">Generations</dt>
                <dd className="tabular-nums text-ink-700">{ctx.generation_counts.completed} ok · {ctx.generation_counts.failed} failed</dd>
                <dt className="text-ink-400">Paid before</dt>
                <dd className="text-ink-700">{ctx.has_transacted ? 'yes' : 'no'}</dd>
              </dl>
              <Link to={`/users/${ctx.user_id}`} className="mt-2 inline-block text-sm text-brand-700 hover:underline">
                Open user profile →
              </Link>

              {ctx.recent_generations.length > 0 && (
                <div className="mt-3">
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-400">Recent generations</h3>
                  <ul className="flex flex-col gap-1.5">
                    {ctx.recent_generations.map((g) => (
                      <li key={g.id} className="flex items-center justify-between gap-2 rounded-md border border-ink-100 px-2.5 py-1.5 text-xs">
                        {/* PX-436: open the full generation detail (PX-433) inline
                            from the ticket, without leaving for the generations list. */}
                        <Link to={`/generations/${g.id}`} className="group block min-w-0">
                          <span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${
                            g.status === 'completed' ? 'bg-emerald-500' : g.status === 'failed' ? 'bg-red-500' : 'bg-amber-400'
                          }`} />
                          <span className="text-ink-700 group-hover:text-brand-700 group-hover:underline">{g.photoshoot_slug}</span>
                          <span className="ml-1 text-ink-400">{new Date(g.created_at).toLocaleDateString()} · {g.credits_used} tk</span>
                          {g.error_message && (
                            <div className="truncate text-red-600" title={g.error_message}>{g.error_message}</div>
                          )}
                        </Link>
                        {canWrite && g.credits_used > 0 && (
                          <button
                            type="button"
                            disabled={refundMutation.isPending}
                            onClick={() => {
                              if (confirm(`Refund ${g.credits_used} tokens for this generation?`)) {
                                refundMutation.mutate({ id: g.id, amount: g.credits_used })
                              }
                            }}
                            className="shrink-0 rounded border border-ink-200 px-2 py-1 font-medium text-ink-700 transition hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                          >
                            Refund
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Section>
          ) : (
            <Section title="Author">
              <p className="text-sm text-ink-400">
                Anonymous web submission — no linked account, reply via email only.
              </p>
            </Section>
          )}
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-500">{title}</h2>
      {children}
    </section>
  )
}
